import { beneficiaries, fmtEur } from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

export function renderBeneficiaries(ctx: Ctx) {
  const totalYtd = beneficiaries.reduce((a, b) => a + b.totalPaidYtd, 0);
  const sepa = beneficiaries.filter((b) => b.rail === "sepa").length;
  const swift = beneficiaries.filter((b) => b.rail === "swift").length;

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Business Account<span class="page-eyebrow-rule"></span>Beneficiaries</span>
        <h1 class="page-title">Saved external recipients</h1>
      </div>
      <div class="page-head-meta" style="flex-direction: row; align-items: center; gap: 0.6rem;">
        <button class="btn btn-ghost btn-sm">${icon("download", 11)} Export</button>
        <button class="btn btn-primary">${icon("plus", 11)} Add beneficiary</button>
      </div>
    </section>

    <section class="summary-row">
      <div class="summary-cell">
        <span class="summary-cell-label">Total beneficiaries</span>
        <span class="summary-cell-value">${beneficiaries.length}</span>
        <span class="summary-cell-note">${sepa} SEPA · ${swift} SWIFT</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Paid YTD</span>
        <span class="summary-cell-value">${fmtEur(totalYtd, { compact: true })}</span>
        <span class="summary-cell-note">All saved recipients combined</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Top recipient</span>
        <span class="summary-cell-value" style="font-size: 1.4rem; letter-spacing: -0.02em;">Vendor 081</span>
        <span class="summary-cell-note">${fmtEur(184_300, { compact: true })} YTD</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Categories</span>
        <span class="summary-cell-value">3</span>
        <span class="summary-cell-note">Suppliers · Services · Logistics</span>
      </div>
    </section>

    <section class="panel">
      <div class="filter-bar">
        <input class="filter-input" type="search" id="ben-search" placeholder="Search name, IBAN or BIC" />
        <select class="filter-select" id="ben-rail">
          <option value="">All rails</option>
          <option value="sepa">SEPA only</option>
          <option value="swift">SWIFT only</option>
        </select>
        <select class="filter-select" id="ben-cat">
          <option value="">All categories</option>
          <option>Suppliers</option>
          <option>Services</option>
          <option>Logistics</option>
        </select>
        <span class="filter-spacer"></span>
        <span class="filter-tag">${beneficiaries.length} saved <button aria-label="Clear">×</button></span>
      </div>
      <div class="ledger-table">
        <div class="ledger-table-header" style="grid-template-columns: 50px minmax(0, 1.4fr) 90px minmax(0, 1.6fr) 110px 130px;">
          <span></span>
          <span>Name</span>
          <span>Rail</span>
          <span>Account</span>
          <span style="text-align: right;">Paid YTD</span>
          <span>Last paid</span>
        </div>
        <div id="ben-rows"></div>
        <div class="ledger-table-foot">
          <span id="ben-foot">Showing ${beneficiaries.length} of ${beneficiaries.length} saved</span>
          <a class="link-arrow" data-route="send">Send to a beneficiary ${icon("arrowRight", 13)}</a>
        </div>
      </div>
    </section>
  `;

  const rowsEl = ctx.canvas.querySelector<HTMLElement>("#ben-rows")!;
  const footEl = ctx.canvas.querySelector<HTMLElement>("#ben-foot")!;
  const search = ctx.canvas.querySelector<HTMLInputElement>("#ben-search")!;
  const rail = ctx.canvas.querySelector<HTMLSelectElement>("#ben-rail")!;
  const cat = ctx.canvas.querySelector<HTMLSelectElement>("#ben-cat")!;

  function row(b: (typeof beneficiaries)[number]) {
    const ident = b.iban || b.swiftBic || "—";
    const initials = b.name
      .split(/[\s·]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("");
    const sealColor =
      b.category === "Suppliers"
        ? "var(--accent)"
        : b.category === "Services"
          ? "var(--state-success)"
          : "var(--state-warning)";
    return `<div class="ledger-table-row" style="grid-template-columns: 50px minmax(0, 1.4fr) 90px minmax(0, 1.6fr) 110px 130px;">
      <div>
        <span class="merchant-seal" style="width: 32px; height: 32px; background: ${sealColor}; color: var(--surface-canvas); font-size: 0.66rem;">${initials}</span>
      </div>
      <div class="col-name" style="min-width: 0;">
        <strong>${b.name}</strong>
        <span>${b.country} · ${b.category}</span>
      </div>
      <div class="col-method">
        <span class="col-method-icon ${b.rail === "sepa" ? "" : "is-card"}">${b.rail[0]?.toUpperCase()}</span>
        ${b.rail.toUpperCase()}
      </div>
      <div class="col-name" style="min-width: 0;">
        <strong style="font-family: ui-monospace, monospace; font-size: 0.78rem; font-weight: 500;">${ident}</strong>
      </div>
      <div class="col-amount is-out" style="font-size: 0.85rem;">${fmtEur(b.totalPaidYtd, { compact: true })}</div>
      <div class="col-status" style="text-transform: none; letter-spacing: 0; font-weight: 500;">${b.lastPaid}</div>
    </div>`;
  }

  function applyFilter() {
    const q = search.value.toLowerCase().trim();
    const r = rail.value;
    const c = cat.value;
    const filtered = beneficiaries.filter((b) => {
      if (r && b.rail !== r) return false;
      if (c && b.category !== c) return false;
      if (q) {
        const hay = `${b.name} ${b.iban ?? ""} ${b.swiftBic ?? ""} ${b.country}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    rowsEl.innerHTML = filtered.length
      ? filtered.map(row).join("")
      : `<div style="padding: 3rem; text-align: center; color: var(--ink-muted); font-size: 0.85rem;">No beneficiaries match your filters.</div>`;
    footEl.textContent = `Showing ${filtered.length} of ${beneficiaries.length} saved`;
  }

  [search, rail, cat].forEach((el) =>
    el.addEventListener("input", applyFilter),
  );
  applyFilter();

  ctx.canvas.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const route = el.getAttribute("data-route") as Route;
      if (route) ctx.navigate(route);
    });
  });
}
